// app/javascript/projects/components/ProjectPickerModal.jsx

import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFilteredProjects, projectKeys } from '../../hooks/projects/queries';
import { useDuplicateProject } from '../../hooks/projects/mutations';
import { useProjectModal } from '../../contexts/ProjectModalContext';
import { useBillingModal } from '../../contexts/BillingModalContext';
import { useSubscription } from '../../hooks/subscriptions/queries';
import { isCancelled, isPastDue } from '../../utils/subscriptionUtils';
import RowModal from './RowModal';
import ModalRow from './ModalRow';
import ProjectRow from './ProjectRow';
import { useHistory, useLocation } from 'react-router-dom';
import { withToastManager } from 'ui/toast';

function ProjectPickerModal(props) {
  const queryClient = useQueryClient();
  const history = useHistory();
  const location = useLocation();
  const isArchived = location.pathname.includes('archived');
  
  // Use the filtered projects hook with isArchived parameter
  const { data: projects = [], isLoading, error } = useFilteredProjects(isArchived);
  
  // Use context for modal visibility
  const { isModalVisible, hideProjectModal } = useProjectModal();
  const { showProjectBlocker, showSubscriptionPastDue } = useBillingModal();
  
  // Use the subscriptions hook
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription();
  const subscriptionCancelled = isCancelled(subscription);
  const subscriptionPastDue = isPastDue(subscription);
  
  // Duplicate project mutation
  const duplicateProjectMutation = useDuplicateProject();
  
  // Error handling for archiving projects
  const errorArchivingProject = useCallback((archive) => {
    hideProjectModal();
    queryClient.invalidateQueries({ queryKey: projectKeys.all });
    
    if (isArchived) {
      history.push('/users');
    }
    
    props.toastManager.add(
      archive ? 'Error Archiving Project - Please Refresh and Try Again' :
      'Error Unarchiving Project - Please Refresh and Try Again', {
        appearance: 'error',
      }
    );
  }, [isArchived, history, props.toastManager, queryClient, hideProjectModal]);
  
  // Hide modal handler
  const onHide = useCallback(() => {
    if (isArchived) {
      history.push('/users');
    } else {
      hideProjectModal();
    }
  }, [isArchived, history, hideProjectModal]);
  
  // Add new project handler
  const addProject = useCallback(() => {
    if (subscriptionLoading) return;
    if (subscriptionPastDue) {
      hideProjectModal();
      showSubscriptionPastDue();
    } else if (subscriptionCancelled) {
      hideProjectModal();
      showProjectBlocker();
    } else {
      hideProjectModal();
      history.push('/projects/new');
    }
  }, [subscriptionLoading, subscriptionCancelled, subscriptionPastDue, showProjectBlocker, showSubscriptionPastDue, history, hideProjectModal]);
  
  // Duplicate project handler
  const duplicateProject = useCallback((projectID) => {
    if (subscriptionLoading) return;
    if (subscriptionPastDue) {
      hideProjectModal();
      showSubscriptionPastDue();
      return;
    } else if (subscriptionCancelled) {
      hideProjectModal();
      showProjectBlocker();
      return;
    }
    
    hideProjectModal();
    
    const toastId = props.toastManager.add(
      'Copying Codes Into New Project', {
        appearance: 'info'
      }
    );
    
    duplicateProjectMutation.mutate(projectID, {
      onSuccess: (data) => {
        history.push(`/projects/${data.id}/analysis`);
        
        props.toastManager.update(
          toastId,
          {
            content: 'New Project Created!',
            appearance: 'success',
            autoDismiss: true,
            autoDismissTimeout: 10000
          }
        );
      },
      onError: () => {
        props.toastManager.update(
          toastId, {
            content: 'Error Creating New Project. Contact Us For Help.',
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 10000
          }
        );
      }
    });
  }, [subscriptionLoading, subscriptionCancelled, subscriptionPastDue, showProjectBlocker, showSubscriptionPastDue, history, props.toastManager, duplicateProjectMutation, hideProjectModal]);
  
  // Determine if modal should be shown
  // For archived view, always show; for regular view, use context state
  const shouldShow = isArchived || isModalVisible;
  
  // Loading state
  if (isLoading) {
    return (
      <RowModal
        bsSize="large"
        show={shouldShow}
        onHide={onHide}
        ariaLabel="project picker"
        header={<h3 style={{marginTop:0}}>{isArchived ? 'Archived Projects' : 'Projects'}</h3>}
      >
        <div style={{textAlign: 'center', padding: '40px'}}>
          Loading projects...
        </div>
      </RowModal>
    );
  }
  
  // Error state
  if (error) {
    return (
      <RowModal
        bsSize="large"
        show={shouldShow}
        onHide={onHide}
        ariaLabel="project picker"
        header={<h3 style={{marginTop:0}}>{isArchived ? 'Archived Projects' : 'Projects'}</h3>}
      >
        <div style={{textAlign: 'center', padding: '40px', color: 'red'}}>
          Error loading projects. Please try again.
        </div>
      </RowModal>
    );
  }
  
  return (
    <div>
      <RowModal
        bsSize="large"
        show={shouldShow}
        onHide={onHide}
        ariaLabel="project picker"
        buttonText='New Project'
        buttonAction={isArchived ? null : addProject}
        header={<h3 style={{marginTop:0}}>{isArchived ? 'Archived Projects' : 'Projects'}</h3>}
      >
        <ModalRow
          row={[
            <strong>Project</strong>,
            <strong>Owner</strong>,
            <strong>Last Edited</strong>,
            ' '
          ]}
        />
        
        {projects.map((project, index) => {
          return <ProjectRow
            project={project}
            key={project.id || index}
            onHide={onHide}
            duplicateProject={duplicateProject}
            errorArchivingProject={errorArchivingProject}
            subscriptionStateLoaded={!subscriptionLoading}
          />
        })}
        
        {projects.length === 0 &&
          <div style={{textAlign: 'center', fontSize: '1.2em', marginTop: '40px'}}>
            {
              isArchived ? 
                "There are no archived projects at this time." :
                "There are no projects at this time."
            }
          </div>
        }
        
      </RowModal>
    </div>
  );
}

export default withToastManager(ProjectPickerModal);